import { useMemo } from 'react';

import { type Workspace } from '@air/api/types';

import { type GeneralLibraryColor } from '~/components/tree/GeneralLibraryTreeItem';
import { useCustomFieldColors } from '~/hooks/api/useCustomFieldColors';
import { useWorkspace } from '~/hooks/api/useWorkspace';

export type UseWorkspaceDetailsParams = {
  workspaceId: Workspace['id'] | undefined;
};

export const useWorkspaceDetails = ({ workspaceId }: UseWorkspaceDetailsParams) => {
  const { data: workspace } = useWorkspace({ workspaceId });
  const { data: colorsData } = useCustomFieldColors({ workspaceId });

  const generalLibraryColor = useMemo<GeneralLibraryColor | undefined>(() => {
    const colorId = workspace?.defaultLibraryColorId;
    if (!colorId || !colorsData?.data) return undefined;

    const color = colorsData.data.find((c) => c.id === colorId);
    if (!color) return undefined;

    return {
      backgroundHex: color.backgroundHex,
      fontHex: color.fontHex,
    };
  }, [workspace?.defaultLibraryColorId, colorsData]);

  return {
    generalLibraryColor,
    generalLibraryDescription: workspace?.defaultLibraryDescription ?? '',
    generalLibraryIcon: workspace?.defaultLibraryIcon ?? '🏠',
    generalLibraryTitle: workspace?.defaultLibrary ?? 'General',
    workspace,
  };
};
